"use client";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/molecules/Table";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  tabSwitched,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

function isExplicitlyOff(v: number | boolean | undefined | null): boolean {
  return v === 0 || v === false;
}

interface ElectiveSubject {
  id?: number;
  semester_id?: number;
  subject_name: string;
  subject_name_text_color?: string;
  credits: number;
  credits_text_color?: string;
  priority?: number;
  status?: number;
}

interface ElectiveSemester {
  id?: number;
  category_id?: number;
  name: string;
  name_color?: string;
  credits_text?: string;
  credits_text_color?: string;
  order_index: number;
  is_active?: number;
  status?: number;
  subjects?: ElectiveSubject[];
}

interface ElectiveCategory {
  id?: number;
  elective_id?: number;
  name: string;
  name_text_color?: string;
  section_color?: string;
  selected_section_color?: string;
  selected_section_strip_color?: string;
  header_section_color?: string;
  subject_container_color?: string;
  order_index: number;
  is_active?: number;
  status?: number;
  semesters?: ElectiveSemester[];
}

type ElectiveSection = {
  title?: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  background_color?: string;
  categories?: ElectiveCategory[];
};

interface Elective1Props {
  data: { data: ElectiveSection } | ElectiveSection;
  className?: string;
}

function getSection(data: Elective1Props["data"] | undefined): ElectiveSection {
  if (!data || typeof data !== "object") return {};
  const root = data as Record<string, unknown>;
  if (Array.isArray(root.categories)) {
    return data as ElectiveSection;
  }
  const once = root.data;
  if (once && typeof once === "object") {
    const inner = once as Record<string, unknown>;
    if (Array.isArray(inner.categories) || inner.title != null) {
      return once as ElectiveSection;
    }
    const twice = inner.data;
    if (twice && typeof twice === "object") {
      return twice as ElectiveSection;
    }
  }
  return (once as ElectiveSection) ?? {};
}

function Elective1({ data, className }: Elective1Props) {
  const ref = useRef<HTMLElement>(null);
  const previousElectiveRef = useRef<string>("");

  const {
    title = "Electives List",
    title_text_color = "#000000",
    sub_title = "",
    sub_title_text_color = "rgba(0, 0, 0, 0.6)",
    background_color = "#FFFFFF",
    categories = [],
  } = getSection(data);

  const electives = useMemo(() => {
    return categories
      .filter(
        (cat) =>
          !isExplicitlyOff(cat.is_active) && !isExplicitlyOff(cat.status),
      )
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  }, [categories]);

  const [selectedElective, setSelectedElective] = useState("");

  useEffect(() => {
    if (electives.length === 0) return;
    setSelectedElective((prev) => {
      if (prev && electives.some((e) => e.name === prev)) return prev;
      const first = electives[0].name;
      if (previousElectiveRef.current === "") {
        previousElectiveRef.current = first;
      }
      return first;
    });
  }, [electives]);

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "elective_section");
  //   }
  // }, []);

  // Track tab switching
  const handleElectiveChange = useCallback((electiveName: string) => {
    // Only track if tab actually changed (not initial render)
    if (
      previousElectiveRef.current !== electiveName &&
      previousElectiveRef.current !== ""
    ) {
      tabSwitched({
        section: "elective_section",
        component: "elective_tabs",
        label: electiveName,
      });
    }
    previousElectiveRef.current = electiveName;
    setSelectedElective(electiveName);
  }, []);

  const currentElectiveData = electives.find(
    (e) => e.name === selectedElective,
  );

  // Get sorted and filtered semesters for current elective
  const currentSemesters = useMemo(() => {
    if (!currentElectiveData?.semesters) return [];
    return currentElectiveData.semesters
      .filter(
        (sem) =>
          !isExplicitlyOff(sem.is_active) && !isExplicitlyOff(sem.status),
      )
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  }, [currentElectiveData]);

  return (
    <section
      ref={ref}
      className={cn(
        "flex justify-center items-center w-full px-5 py-8 sm:px-8 sm:py-11",
        className,
      )}
      style={{ backgroundColor: background_color }}
    >
      <div
        className={cn(
          "flex flex-col gap-6 w-full",
          "sm:w-[1008px] md:w-[1158px] lg:w-[1358px] xl:w-[1678px]",
        )}
      >
        <div
          className={cn(
            "flex flex-col gap-2 w-full",
            "xs:items-center xs:text-center",
            "sm:items-start sm:text-left",
          )}
        >
          <h2
            className={cn(
              "text-heading-6-semibold break-words w-full",
              "sm:text-heading-5-semibold",
              "md:text-heading-4-semibold",
              "lg:text-new-heading-34-semibold",
              "xl:text-new-heading-42-semibold",
              "line-clamp-1",
              "type-h3",
            )}
            style={{ color: title_text_color }}
          >
            {title}
          </h2>
          <p
            className={cn(
              "text-body-3-regular break-words w-full",
              "md:text-body-2-regular",
              "xl:text-new-body-24-regular",
              "line-clamp-2 sm:line-clamp-1",
              "type-bdy-sm",
            )}
            style={{ color: sub_title_text_color }}
          >
            {sub_title}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-row gap-0 overflow-x-auto p-2 -mx-2 sm:mx-0">
          {electives?.length > 0 &&
            electives?.map((elective) => {
              const isSelected = selectedElective === elective.name;
              return (
                <button
                  key={elective.name}
                  onClick={() => handleElectiveChange(elective.name)}
                  className={cn(
                    "px-4 py-3 sm:px-6 whitespace-nowrap transition-all relative shrink-0",
                    "xs:text-heading-8-semibold xs:px-[22px] xs:py-2 ",
                    "sm:text-heading-8-semibold",
                    "md:text-heading-7-semibold",
                    "lg:text-heading-7-semibold",
                    "xl:text-heading-6-semibold",
                    "type-h6",
                  )}
                  style={{
                    backgroundColor: isSelected
                      ? elective.selected_section_color || "#E8E8E8"
                      : elective.section_color || "#F5F5F5",
                    color:
                      elective.name_text_color ||
                      (isSelected ? "#000000" : "rgba(0, 0, 0, 0.7)"),
                  }}
                >
                  {isSelected && (
                    <div
                      className="absolute left-0 top-0 bottom-0 w-1 rounded-r type-body-sm"
                      style={{
                        backgroundColor:
                          elective.selected_section_strip_color || "#BE2330",
                      }}
                    />
                  )}
                  {elective.name}
                </button>
              );
            })}
        </div>

        {/* Semester Tables */}
        <div
          className={cn(
            "grid w-full",
            "gap-6",
            " sm:gap-[46px]",
            currentSemesters.length > 1
              ? "grid-cols-1 sm:grid-cols-2"
              : "grid-cols-1 sm:max-w-4xl md:max-w-5xl lg:max-w-6xl xl:max-w-7xl",
          )}
        >
          {currentSemesters.map((semester, semesterIndex) => {
            const subjects = (semester.subjects || [])
              .filter((subj) => !isExplicitlyOff(subj.status))
              .sort((a, b) => (a.priority || 0) - (b.priority || 0));

            return (
              <div
                key={semester.id || semesterIndex}
                className={cn(
                  "border border-gray-200",
                  "p-3 w-full overflow-hidden",
                  "xs:rounded-[10px]",
                  "sm:rounded-[10px]",
                )}
                style={{
                  backgroundColor:
                    currentElectiveData?.subject_container_color || "#FFFFFF",
                }}
              >
                <div className={cn("overflow-x-auto smooth-scroll w-full")}>
                  <Table className="min-w-full">
                    <TableHeader
                      style={{
                        backgroundColor:
                          currentElectiveData?.header_section_color ||
                          "#BE2330",
                      }}
                    >
                      <TableRow className="border-0 hover:bg-transparent">
                        <TableHead
                          className={cn(
                            "rounded-tl-4 break-words",
                            "text-heading-9-semibold",
                            "md:text-heading-8-semibold",
                            "lg:py-3 lg:px-[14px] ",
                            "xl:pl-[14px] xl:py-[12px] xl:text-heading-6-semibold",
                            "type-h6",
                          )}
                          style={{ color: semester.name_color || "#FFFFFF" }}
                        >
                          {semester.name}
                        </TableHead>
                        <TableHead
                          className={cn(
                            "text-right rounded-tr-4 whitespace-nowrap",
                            "text-heading-9-semibold",
                            "md:text-heading-8-semibold  ",
                            "w-24 shrink-0",
                            "lg:pr-[14px]",
                            "xl:pr-[14px] xl:py-[12px] xl:text-heading-6-semibold",
                            "type-h6",
                          )}
                          style={{
                            color: semester.credits_text_color || "#FFFFFF",
                          }}
                        >
                          {semester.credits_text || "Credits"}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody
                      className={cn("border-none [&>tr:last-child]:border-0")}
                    >
                      {subjects.map((subject, subjectIndex) => (
                        <TableRow
                          key={subject.id || subjectIndex}
                          className={cn(
                            "text-start hover:bg-transparent border-0",
                          )}
                        >
                          <TableCell
                            className={cn(
                              "text-heading-9-regular break-words",
                              "md:text-heading-8-regular",
                              "lg:pb-[22px]",
                              "xl:text-new-body-24-regular xl:p-[14px]",
                              "max-w-[200px] sm:max-w-none",
                              "type-body-sm",
                              {
                                "lg:pt-3": subjectIndex === 0,
                              },
                            )}
                            style={{
                              color:
                                subject.subject_name_text_color ||
                                "rgba(0, 0, 0, 0.8)",
                            }}
                          >
                            {subjectIndex + 1}. {subject.subject_name}
                          </TableCell>
                          <TableCell
                            className={cn(
                              "text-heading-8-medium text-right whitespace-nowrap",
                              "xs:text-heading-9-semibold",
                              "sm:text-heading-9-semibold",
                              "md:text-heading-8-semibold",
                              "xl:text-new-body-24-semibold",
                              "w-24 shrink-0",
                              "type-h6",
                            )}
                            style={{
                              color:
                                subject.credits_text_color ||
                                "rgba(0, 0, 0, 0.8)",
                            }}
                          >
                            {subject.credits}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default Elective1;
